// =============================================================================
// Inventory Management — purchase lots and their ledger.
//
// A lot is one purchase order (or, for carry-over stock, the contract it came in under). It carries the
// cost basis the whole module values stock at, and the reductions posted against it are what turn that
// basis into a book value the finance team can reconcile.
// =============================================================================

import React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { strings } from '/imports/strings';
import { PageHeading } from '/imports/ui/PageHeading';
import { PageBody } from '/imports/ui/PageBody';
import { NotPermittedPage } from '/imports/ui/NotPermittedPage';
import { LoadingFlex } from '/imports/ui/LoadingFlex';
import { Button } from '/imports/ui/Button';
import { useMethod } from '/imports/hooks';
import {
  inventoryFeocStatusLabel, inventoryListLots, inventoryLotDetail, inventoryPalletStatusLabel,
} from '/imports/api/inventory';
import {
  EmptyRow, Fact, InventoryPageStyled, Tile, count, day, money, orDash, useInventoryAccess,
} from '/imports/ui/InventoryShared';

/** Every purchase lot with its procurement economics and how far it has been drawn down. */
export const InventoryLotsPage = () => {
  const { canAccess } = useInventoryAccess();
  const navigate = useNavigate();
  const [rows, loading] = useMethod(inventoryListLots, undefined, []);
  if (!canAccess) return <NotPermittedPage />;

  const totals = (rows || []).reduce((t, l) => ({
    modulesOrdered: t.modulesOrdered + (l.modulesOrdered || 0),
    palletsOrdered: t.palletsOrdered + (l.palletsOrdered || 0),
    basePrice: t.basePrice + l.basePrice,
    salesTax: t.salesTax + l.salesTax,
    totalCost: t.totalCost + l.totalCost,
    modulesAllocated: t.modulesAllocated + l.modulesAllocated,
    modulesRemaining: t.modulesRemaining + l.modulesRemaining,
  }), { modulesOrdered: 0, palletsOrdered: 0, basePrice: 0, salesTax: 0, totalCost: 0, modulesAllocated: 0, modulesRemaining: 0 });

  return (
    <InventoryPageStyled className="page">
      <PageHeading left={<h1>{strings.InventoryLots()}</h1>} />
      <PageBody>
        <div className="scroll-x">
          <table className="inv">
            <thead>
              <tr>
                <th>{strings.InventoryPo()}</th>
                <th>{strings.InventoryContractNumber()}</th>
                <th>{strings.Vendor()}</th>
                <th>{strings.InventoryShipTo()}</th>
                <th>{strings.InventoryModuleModel()}</th>
                <th className="num">{strings.InventoryWattage()}</th>
                <th className="num">{strings.InventoryModulesOrdered()}</th>
                <th className="num">{strings.InventoryPalletsOrdered()}</th>
                <th className="num">{strings.InventoryUnitCost()}</th>
                <th className="num">{strings.InventorySalesTaxRate()}</th>
                <th className="num">{strings.InventoryBasePrice()}</th>
                <th className="num">{strings.InventorySalesTax()}</th>
                <th className="num">{strings.InventoryTotalCost()}</th>
                <th className="num">{strings.InventoryModulesAllocated()}</th>
                <th className="num">{strings.InventoryModulesRemaining()}</th>
                <th>{strings.InventoryGdd()}</th>
                <th>{strings.InventoryFeocStatus()}</th>
              </tr>
            </thead>
            <tbody>
              {loading && !rows && <tr><td colSpan={17}><LoadingFlex /></td></tr>}
              {(rows || []).map((l) => (
                <tr key={l._id} className="clickable" onClick={() => navigate(`/inventory/lots/${l._id}`)}>
                  <td>{orDash(l.poNumber)}</td>
                  <td>{l.contractNumber}</td>
                  <td>{orDash(l.vendorName)}</td>
                  <td>{orDash(l.warehouseCode)}</td>
                  <td>{orDash(l.moduleModel)}</td>
                  <td className="num">{count(l.wattage)}</td>
                  <td className="num">{count(l.modulesOrdered)}</td>
                  <td className="num">{count(l.palletsOrdered)}</td>
                  <td className="num">{l.costPerUnit == null ? orDash(undefined) : money(l.costPerUnit, true)}</td>
                  <td className="num">{l.salesTaxRate == null ? orDash(undefined) : `${(l.salesTaxRate * 100).toFixed(2)}%`}</td>
                  <td className="num">{money(l.basePrice)}</td>
                  <td className="num">{money(l.salesTax)}</td>
                  <td className="num">{money(l.totalCost)}</td>
                  <td className="num">{count(l.modulesAllocated)}</td>
                  <td className="num">{count(l.modulesRemaining)}</td>
                  <td>{day(l.gdd)}</td>
                  <td>{inventoryFeocStatusLabel(l.feocStatus)}</td>
                </tr>
              ))}
              {!loading && !rows?.length && <EmptyRow colSpan={17} />}
            </tbody>
            {!!rows?.length && (
              <tfoot>
                <tr>
                  <td colSpan={6}>{strings.Total()}</td>
                  <td className="num">{count(totals.modulesOrdered)}</td>
                  <td className="num">{count(totals.palletsOrdered)}</td>
                  <td colSpan={2} />
                  <td className="num">{money(totals.basePrice)}</td>
                  <td className="num">{money(totals.salesTax)}</td>
                  <td className="num">{money(totals.totalCost)}</td>
                  <td className="num">{count(totals.modulesAllocated)}</td>
                  <td className="num">{count(totals.modulesRemaining)}</td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </PageBody>
    </InventoryPageStyled>
  );
};

/** One lot: its procurement facts, its book value and the reductions posted against it. */
export const InventoryLotPage = () => {
  const { _id } = useParams<{ _id: string }>();
  const { canAccess } = useInventoryAccess();
  const [detail, loading] = useMethod(inventoryLotDetail, { lotId: _id as string }, [_id]);
  if (!canAccess) return <NotPermittedPage />;
  if (loading && !detail) return <InventoryPageStyled className="page"><PageBody><LoadingFlex /></PageBody></InventoryPageStyled>;
  if (!detail) return <InventoryPageStyled className="page"><PageBody><div className="empty">{strings.NoResults()}</div></PageBody></InventoryPageStyled>;

  const { row, warehouse, reductions, palletsByStatus } = detail;
  const title = row.poNumber ? `${strings.InventoryPo()} ${row.poNumber}` : row.contractNumber;

  return (
    <InventoryPageStyled className="page">
      <PageHeading
        left={<h1>{title}</h1>}
        right={<Link to={`/inventory/pallet-inventory?lot=${row._id}`}><Button className="secondary compact">{strings.InventoryOpenInPalletInventory()}</Button></Link>}
      />
      <PageBody>
        <div className="tiles">
          <Tile label={strings.InventoryModulesOrdered()} value={count(row.modulesOrdered)} sub={`${count(row.palletsOrdered)} ${strings.InventoryTotalPallets().toLowerCase()}`} />
          <Tile label={strings.InventoryTotalCost()} value={money(row.totalCost)} sub={`${strings.InventoryBasePrice()} ${money(row.basePrice)}`} />
          <Tile label={strings.InventoryLandedUnitPrice()} value={money(row.landedUnitPrice, true)} sub={row.costPerUnit == null ? strings.InventoryCostBasisUnknown() : `${strings.InventoryUnitCost()} ${money(row.costPerUnit, true)}`} />
          <Tile label={strings.InventoryBookUnits()} value={count(row.bookUnits)} sub={`${count(row.reducedUnits)} ${strings.InventoryReductions().toLowerCase()}`} />
          <Tile label={strings.InventoryBookValue()} value={money(row.bookValue)} />
          <Tile label={strings.InventoryModulesRemaining()} value={count(row.modulesRemaining)} sub={`${count(row.palletsRemaining)} ${strings.InventoryPalletsRemaining().toLowerCase()}`} />
        </div>

        <div className="panels">
          <div className="panel-card">
            <h2>{strings.Vendor()}</h2>
            <div className="facts">
              <Fact label={strings.Name()} value={row.vendorName} />
              <Fact label={strings.InventoryContractNumber()} value={row.contractNumber} />
              <Fact label={strings.InventoryVendorInvoiceNumbers()} value={row.vendorInvoiceNumbers?.join(', ')} />
              <Fact label={strings.InventoryPaymentTerms()} value={row.paymentTerms} />
              <Fact label={strings.InventoryShippingTerms()} value={row.shippingTerms} />
            </div>
          </div>
          <div className="panel-card">
            <h2>{strings.Equipment()}</h2>
            <div className="facts">
              <Fact label={strings.InventoryModuleModel()} value={row.moduleModel} />
              <Fact label={strings.InventoryWattage()} value={row.wattage ? `${row.wattage} W` : undefined} />
              <Fact label={strings.InventoryModulesPerPallet()} value={count(row.modulesPerPallet)} />
              <Fact label={strings.InventoryTotalNameplate()} value={`${row.mw.toFixed(2)} MW`} />
              <Fact label={strings.InventoryPricePerWatt()} value={row.pricePerWatt == null ? undefined : money(row.pricePerWatt, true)} />
              <Fact label={strings.InventoryShipTo()} value={warehouse ? <Link to={`/inventory/warehouses/${warehouse._id}`}>{warehouse.code} — {warehouse.city}</Link> : undefined} />
            </div>
          </div>
          <div className="panel-card">
            <h2>{strings.InventoryFeocStatus()}</h2>
            <div className="facts">
              <Fact label={strings.InventoryDomesticContent()} value={row.domesticContentPct == null ? undefined : `${(row.domesticContentPct * 100).toFixed(1)}%`} />
              <Fact label={strings.InventoryFeocStatus()} value={inventoryFeocStatusLabel(row.feocStatus)} />
              <Fact label={strings.InventoryEdd()} value={day(row.edd)} />
              <Fact label={strings.InventoryGdd()} value={day(row.gdd)} />
            </div>
          </div>
          <div className="panel-card">
            <h2>{strings.InventoryByStatus()}</h2>
            <table className="inv">
              <tbody>
                {palletsByStatus.map((s) => (
                  <tr key={s.status}>
                    <td>{inventoryPalletStatusLabel(s.status)}</td>
                    <td className="num">{count(s.count)}</td>
                    <td className="num">{count(s.modules)}</td>
                  </tr>
                ))}
                {!palletsByStatus.length && <EmptyRow colSpan={3} label={strings.InventoryNoPallets()} />}
              </tbody>
            </table>
          </div>
        </div>

        <h2 style={{ fontSize: '0.9rem', marginTop: '1.2rem' }}>{strings.InventoryReductions()}</h2>
        <div className="scroll-x">
          <table className="inv">
            <thead>
              <tr>
                <th>{strings.InventoryCountColumn()}</th>
                <th>{strings.Customer()}</th>
                <th>{strings.Address()}</th>
                <th>{strings.State()}</th>
                <th>{strings.Date()}</th>
                <th className="num">{strings.Quantity()}</th>
                <th className="num">{strings.InventoryLandedUnitPrice()}</th>
                <th className="num">{strings.InventoryReductionAmount()}</th>
                <th>{strings.InventoryTaxJurisdiction()}</th>
                <th className="num">{strings.InventorySalesTaxPaid()}</th>
                <th>{strings.InventoryUseTaxDue()}</th>
                <th className="num">{strings.InventoryUseTaxRate()}</th>
                <th className="num">{strings.InventoryTaxLiability()}</th>
                <th>{strings.InventoryPaidDate()}</th>
              </tr>
            </thead>
            <tbody>
              {reductions.map((r) => (
                <tr key={r._id}>
                  <td>{r.count}</td>
                  <td>{orDash(r.customer)}</td>
                  <td>{orDash(r.address)}</td>
                  <td>{orDash(r.state)}</td>
                  <td>{day(r.date)}</td>
                  <td className="num">{count(r.quantity)}</td>
                  <td className="num">{money(r.price, true)}</td>
                  <td className="num">{money(r.amount, true)}</td>
                  <td>{orDash(r.taxJurisdiction)}</td>
                  <td className="num">{money(r.salesTaxPaid, true)}</td>
                  <td>{r.useTaxDue ? strings.Yes() : strings.No()}</td>
                  <td className="num">{r.useTaxRate == null ? orDash(undefined) : `${(r.useTaxRate * 100).toFixed(2)}%`}</td>
                  <td className="num">{money(r.taxLiability, true)}</td>
                  <td>{day(r.paidDate)}</td>
                </tr>
              ))}
              {!reductions.length && <EmptyRow colSpan={14} label={strings.InventoryNoReductions()} />}
            </tbody>
            {!!reductions.length && (
              <tfoot>
                <tr>
                  <td colSpan={5}>{strings.Total()}</td>
                  <td className="num">{count(row.reducedUnits)}</td>
                  <td />
                  <td className="num">{money(row.reducedAmount, true)}</td>
                  <td colSpan={6} />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        <div className="note">{strings.InventoryTaxLiabilityNote()}</div>
      </PageBody>
    </InventoryPageStyled>
  );
};
